import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import { TargetData, NightMetadata } from "../types";

interface TargetInfoCardProps {
  selectedTarget: TargetData;
  selectedNight: string | "total";
  getNightMeta: (
    target: TargetData,
    field:
      | keyof NightMetadata["acquisitionDetails"]
      | keyof NightMetadata["location"]
      | "cameraType",
    selectedNight?: string | "total"
  ) => (string | number)[] | "—";
  formatMultipleValues: (
    values: (string | number)[] | "—",
    unit?: string
  ) => React.ReactNode;
}

const TargetInfoCard: React.FC<TargetInfoCardProps> = ({
  selectedTarget,
  selectedNight,
  getNightMeta,
  formatMultipleValues,
}) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      borderRadius: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      p: 3,
      border: "1px solid",
      borderColor: "divider",
      minHeight: 340,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      flex: 1,
      height: "100%",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        mb: 2,
        display: "flex",
        alignItems: "center",
        color: "error.main",
      }}
    >
      <span style={{ marginRight: 8 }}>📍</span> Target Information
    </Typography>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography color="text.secondary">Object Name</Typography>
      <Typography fontWeight={600}>{selectedTarget.name}</Typography>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography color="text.secondary">RA</Typography>
      <Typography fontWeight={600}>
        {formatMultipleValues(
          getNightMeta(selectedTarget, "RACoordinates", selectedNight)
        )}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography color="text.secondary">DEC</Typography>
      <Typography fontWeight={600}>
        {formatMultipleValues(
          getNightMeta(selectedTarget, "DECCoordinates", selectedNight)
        )}
      </Typography>
    </Box>
    <Divider sx={{ my: 1.5, bgcolor: "divider" }} />
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography color="text.secondary">Observation Site</Typography>
      <Typography fontWeight={600}>
        {formatMultipleValues(
          getNightMeta(selectedTarget, "name", selectedNight)
        )}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography color="text.secondary">Bortle Scale</Typography>
      <Box fontWeight={600} component="span">
        {formatMultipleValues(
          getNightMeta(selectedTarget, "bortle", selectedNight)
        )}
      </Box>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography color="text.secondary">Elevation</Typography>
      <Box fontWeight={600} component="span">
        {formatMultipleValues(
          getNightMeta(selectedTarget, "elevation", selectedNight),
          "m"
        )}
      </Box>
    </Box>
  </Box>
);

export default TargetInfoCard;
